import { cssVar } from '@mfw/common';
import type { AvatarGroupProps } from '@mui/material/AvatarGroup';
import type { AvatarProps } from '@mui/material/Avatar';
import type { Theme } from '@mui/material/styles';

import { avatarGroupClasses } from '@mui/material/AvatarGroup';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = [
  'default',
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

const colorByName = (name: string) => {
  const charAt = name.charAt(0).toLowerCase();

  if (['a', 'c', 'f'].includes(charAt)) return 'primary';
  if (['d', 'e', 'h'].includes(charAt)) return 'secondary';
  if (['i', 'k', 'l'].includes(charAt)) return 'info';
  if (['m', 'n', 'p'].includes(charAt)) return 'success';
  if (['q', 's', 't'].includes(charAt)) return 'warning';
  if (['v', 'x', 'y'].includes(charAt)) return 'error';
  return 'default';
};

// NEW VARIANT
declare module '@mui/material/AvatarGroup' {
  interface AvatarGroupPropsVariantOverrides {
    compact: true;
  }
}

// ----------------------------------------------------------------------

export function avatar(theme: Theme) {
  return {
    MuiAvatar: {
      styleOverrides: {
        colorDefault: ({ ownerState }: { ownerState: AvatarProps }) => {
          const color = ownerState.alt
            ? colorByName(`${ownerState.alt}`)
            : 'default';

          // The `default` pair applies with OR without an `alt`. MUI's own
          // `colorDefault` paints initials in `background.default` on a grey
          // disc — legible only while the surfaces are dark (1.47:1 on a cream
          // tenant) — and the old `alt`-keyed branch left every raw
          // `@mui/material/Avatar` without an `alt` on exactly that pair.
          return color !== 'default'
            ? {
                backgroundColor: theme.palette[color].main,
                color: theme.palette[color].contrastText,
              }
            : {
                backgroundColor: alpha(theme.palette.grey[500], 0.24),
                color: cssVar.text.secondary,
              };
        },
        rounded: {
          borderRadius: '50%',
        },
      },

      variants: COLORS.map((color) =>
        color === 'default'
          ? {
              props: { color: 'default' },
              style: {
                backgroundColor: alpha(theme.palette.grey[500], 0.24),
                color: cssVar.text.secondary,
              },
            }
          : {
              props: { color },
              style: {
                backgroundColor: theme.palette[color].main,
                color: theme.palette[color].contrastText,
              },
            },
      ),
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          '&:first-of-type': {
            backgroundColor: cssVar.primary.lighter,
            color: cssVar.primary.dark,
            fontSize: 12,
          },
          fontSize: 16,
          fontWeight: theme.typography.fontWeightSemiBold,
        },
        root: ({ ownerState }: { ownerState: AvatarGroupProps }) => ({
          justifyContent: 'flex-end',
          ...(ownerState.variant === 'compact' && {
            [`& .${avatarGroupClasses.avatar}`]: {
              '&:first-of-type': {
                bottom: 0,
                left: 0,
                zIndex: 9,
              },
              '&:last-of-type': {
                right: 0,
                top: 0,
              },
              height: 28,
              margin: 0,
              position: 'absolute',
              width: 28,
            },
            height: 40,
            position: 'relative',
            width: 40,
          }),
        }),
      },
    },
  };
}
